import React, { useState } from 'react';
import useLanguage from '../../../../../customHooks/useLanguage';
import useTranslate from '../../../../../customHooks/useTranslate';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import IconButton from '../../../../../muiComponents/IconButton';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import { useZitoneoHotelPresentation } from '../../../contexts/ZitoneoContentContext';
import { getCompetitorAnalysisFile } from '../../../services/competitorServices';
import { CompetitorAnalysisComparison } from '../../../types/competitors';
import CompetitorViewerPanel from './CompetitorViewerPanel';
import NoCompetitorsHistory from './NoCompetitorsHistory';

type ScrapingResult = ReturnType<
    typeof useZitoneoHotelPresentation
>['scrapingResults'][number];

const CompetitorsHistory = () => {
    const translate = useTranslate();
    const language = useLanguage();
    const { setErrorMessage } = useNotification();
    const { isLoading, scrapingResults } = useZitoneoHotelPresentation();
    const [analysis, setAnalysis] =
        useState<CompetitorAnalysisComparison | null>(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [loadingResultKey, setLoadingResultKey] = useState<string | null>(
        null
    );

    const handleView = async (result: ScrapingResult) => {
        if (loadingResultKey) {
            return;
        }
        setLoadingResultKey(`${result.Id}-${result.IdSection}`);
        try {
            setAnalysis(await getCompetitorAnalysisFile(result.IdFile));
            setIsViewerOpen(true);
        } catch (error) {
            console.error('Could not load competitor analysis:', error);
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setLoadingResultKey(null);
        }
    };

    const handleViewerClose = () => {
        setIsViewerOpen(false);
        setAnalysis(null);
    };

    const dateFormatter = new Intl.DateTimeFormat(language, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });

    return (
        <div className='service-scraper__history' aria-busy={isLoading}>
            {isLoading ? (
                <LoadingPlaceholder
                    variant='rounded'
                    width='100%'
                    height={80}
                    aria-hidden='true'
                />
            ) : scrapingResults.length ? (
                <ul className='service-scraper__history-list'>
                    {scrapingResults.map(result => (
                        <li
                            key={`${result.Id}-${result.IdSection}`}
                            className='service-scraper__history-row service-scraper__history-row--columns'
                        >
                            <div>
                                <time
                                    dateTime={result.ExecutionDate.toISOString()}
                                >
                                    {dateFormatter.format(result.ExecutionDate)}
                                </time>
                                <p className='service-scraper__history-status'>
                                    {result.ProcessStatus ||
                                        (!result.Result
                                            ? translate('failed')
                                            : '')}
                                </p>
                            </div>
                            <span className='service-scraper__history-file'>
                                {result.File}
                            </span>
                            <div className='flex gap-5'>
                                <IconButton
                                    color='secondary'
                                    iconName={commonIcons.VIEW}
                                    aria-label={translate('view_result')}
                                    disabled={
                                        !result.IdFile ||
                                        Boolean(loadingResultKey)
                                    }
                                    onClick={() => handleView(result)}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <NoCompetitorsHistory />
            )}
            <CompetitorViewerPanel
                open={isViewerOpen}
                onClose={handleViewerClose}
                analysis={analysis}
            />
        </div>
    );
};

export default CompetitorsHistory;
